import {
  Air as AirIcon,
  Cloud as CloudIcon,
  FullscreenExit as FullscreenExitIcon,
  Fullscreen as FullscreenIcon,
  GroupWork as GroupWorkIcon,
  LocalShipping as VehiclesIcon,
  Map as MapIcon,
  OpacityOutlined as OpacityIcon,
  RvHookup as TrailersIcon,
  Satellite as SatelliteIcon,
  Schedule as ScheduleIcon,
  Settings as SettingsIcon,
  Speed as SpeedIcon,
  Store as StoreIcon,
  Terrain as TerrainIcon,
  Traffic as TrafficIcon,
  WbSunny as WbSunnyIcon,
  ZoomIn as ZoomInIcon,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Slider,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { MAP_TYPES, MapControlsProps } from "../../../types/map/types";

const getMapTypeIcon = (type: string) => {
  switch (type.toLowerCase()) {
    case "satellite":
      return <SatelliteIcon />;
    case "hybrid":
      return <SatelliteIcon />;
    case "terrain":
      return <TerrainIcon />;
    default:
      return <MapIcon />;
  }
};

const MapControls: React.FC<MapControlsProps> = ({
  options,
  onMapTypeChange,
  onFullscreenToggle,
  onClustersToggle,
  onVehiclePinsToggle,
  onShopsToggle,
  onTrafficToggle,
  onTimeZonesToggle,
  onAnyTrackToggle,
  onWeatherLayerChange,
  onZoomSensitivityChange,
}) => {
  const [showSettings, setShowSettings] = useState(false);
  const theme = useTheme();
  const sectionTitleSx = {
    color: theme.palette.text.primary,
    fontSize: "0.75rem",
    fontWeight: 600,
    lineHeight: 1.2,
    mb: 0.5,
  };
  const listItemIconSx = {
    color: theme.palette.text.primary,
    minWidth: 28,
    "& .MuiSvgIcon-root": {
      fontSize: 18,
    },
  };
  const listItemTextSx = {
    "& .MuiListItemText-primary": {
      color: theme.palette.text.primary,
      fontSize: "0.78rem",
      lineHeight: 1.2,
    },
  };
  const dividerSx = {
    my: 1,
    borderColor:
      theme.palette.mode === "light"
        ? "rgba(0, 0, 0, 0.12)"
        : "rgba(255, 255, 255, 0.12)",
  };

  const getWeatherIcon = (type: string) => {
    switch (type) {
      case "precipitation":
        return <OpacityIcon />;
      case "clouds":
        return <CloudIcon />;
      case "temperature":
        return <WbSunnyIcon />;
      case "wind":
        return <AirIcon />;
      case "pressure":
        return <SpeedIcon />;
      default:
        return <CloudIcon />;
    }
  };

  const controlButtonSx = {
    backgroundColor:
      theme.palette.mode === "light"
        ? "rgba(255, 255, 255, 0.9)"
        : "rgba(33, 33, 33, 0.9)",
    color: theme.palette.text.primary,
    "&:hover": {
      backgroundColor:
        theme.palette.mode === "light"
          ? "rgba(255, 255, 255, 0.8)"
          : "rgba(33, 33, 33, 0.8)",
    },
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    width: 34,
    height: 34,
    "& .MuiSvgIcon-root": {
      fontSize: 20,
    },
  };

  return (
    <Box
      sx={{
        position: "absolute",
        top: 8,
        right: 8,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
      }}
    >
      <IconButton
        onClick={() => setShowSettings(!showSettings)}
        sx={controlButtonSx}
      >
        <SettingsIcon />
      </IconButton>

      <Collapse in={showSettings}>
        <Card
          sx={{
            mt: 0.75,
            width: 196,
            backgroundColor:
              theme.palette.mode === "light"
                ? "rgba(255, 255, 255, 0.9)"
                : "rgba(33, 33, 33, 0.9)",
            color: theme.palette.text.primary,
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
          }}
        >
          <CardContent
            sx={{
              p: 1,
              "&:last-child": { pb: 1 },
              "& .MuiSwitch-root": {
                transform: "scale(0.86)",
                transformOrigin: "right center",
              },
            }}
          >
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                gutterBottom
                sx={sectionTitleSx}
              >
                Map Type
              </Typography>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                {Object.entries(MAP_TYPES).map(([key, value]) => (
                  <IconButton
                    key={key}
                    onClick={() => onMapTypeChange(value)}
                    color={
                      options.currentMapType === value ? "primary" : "default"
                    }
                    size="small"
                    sx={{
                      color: theme.palette.text.primary,
                      width: 30,
                      height: 30,
                      padding: 0.5,
                      "& .MuiSvgIcon-root": {
                        fontSize: 18,
                      },
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "light"
                            ? "rgba(0, 0, 0, 0.04)"
                            : "rgba(255, 255, 255, 0.08)",
                      },
                    }}
                  >
                    {getMapTypeIcon(value)}
                  </IconButton>
                ))}
              </Box>
            </Box>

            <Divider sx={dividerSx} />

            <Typography
              variant="caption"
              gutterBottom
              sx={sectionTitleSx}
            >
              Display Options
            </Typography>
            <List sx={{ py: 0 }}>
              <ListItem sx={{ py: 0.125, px: 0 }}>
                <ListItemIcon sx={listItemIconSx}>
                  {options.isFullscreen ? (
                    <FullscreenExitIcon />
                  ) : (
                    <FullscreenIcon />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary="Fullscreen"
                  sx={listItemTextSx}
                />
                <Switch
                  edge="end"
                  size="small"
                  checked={options.isFullscreen}
                  onChange={onFullscreenToggle}
                />
              </ListItem>

              <ListItem sx={{ py: 0.125, px: 0 }}>
                <ListItemIcon sx={listItemIconSx}>
                  <TrafficIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Traffic"
                  sx={listItemTextSx}
                />
                <Switch
                  edge="end"
                  size="small"
                  checked={options.showTraffic}
                  onChange={onTrafficToggle}
                />
              </ListItem>

              <ListItem sx={{ py: 0.125, px: 0 }}>
                <ListItemIcon sx={listItemIconSx}>
                  <ScheduleIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Time Zones"
                  sx={listItemTextSx}
                />
                <Switch
                  edge="end"
                  size="small"
                  checked={options.showTimeZones}
                  onChange={onTimeZonesToggle}
                />
              </ListItem>
            </List>

            <Divider sx={dividerSx} />

            <Typography
              variant="caption"
              gutterBottom
              sx={sectionTitleSx}
            >
              Map Pins
            </Typography>
            <List sx={{ py: 0 }}>
              <ListItem sx={{ py: 0.125, px: 0 }}>
                <ListItemIcon sx={listItemIconSx}>
                  <VehiclesIcon />
                </ListItemIcon>
                <ListItemText primary="Vehicles" sx={listItemTextSx} />
                <Switch
                  edge="end"
                  size="small"
                  checked={options.showVehicles}
                  onChange={onVehiclePinsToggle}
                />
              </ListItem>

              <ListItem sx={{ py: 0.125, px: 0 }}>
                <ListItemIcon sx={listItemIconSx}>
                  <GroupWorkIcon />
                </ListItemIcon>
                <ListItemText primary="Clusters" sx={listItemTextSx} />
                <Switch
                  edge="end"
                  size="small"
                  checked={options.showClusters}
                  onChange={onClustersToggle}
                />
              </ListItem>

              <ListItem sx={{ py: 0.125, px: 0 }}>
                <ListItemIcon sx={listItemIconSx}>
                  <StoreIcon />
                </ListItemIcon>
                <ListItemText primary="Shops" sx={listItemTextSx} />
                <Switch
                  edge="end"
                  size="small"
                  checked={options.showShops}
                  onChange={onShopsToggle}
                />
              </ListItem>

              <ListItem sx={{ py: 0.125, px: 0 }}>
                <ListItemIcon sx={listItemIconSx}>
                  <TrailersIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Trailers"
                  sx={listItemTextSx}
                />
                <Switch
                  edge="end"
                  size="small"
                  checked={options.showAnyTrackVehicles}
                  onChange={onAnyTrackToggle}
                />
              </ListItem>
            </List>

            <Divider sx={dividerSx} />

            <Typography
              variant="caption"
              gutterBottom
              sx={sectionTitleSx}
            >
              Weather Layers
            </Typography>
            <List sx={{ py: 0 }}>
              {options.weatherLayers.map((layer, index) => (
                <ListItem key={layer.type} sx={{ py: 0.125, px: 0 }}>
                  <ListItemIcon sx={listItemIconSx}>
                    {getWeatherIcon(layer.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      layer.type.charAt(0).toUpperCase() + layer.type.slice(1)
                    }
                    sx={listItemTextSx}
                  />
                  <Switch
                    edge="end"
                    size="small"
                    checked={layer.isVisible}
                    onChange={() =>
                      onWeatherLayerChange(index, {
                        ...layer,
                        isVisible: !layer.isVisible,
                      })
                    }
                  />
                </ListItem>
              ))}
            </List>

            <Divider sx={dividerSx} />

            <Typography
              variant="caption"
              gutterBottom
              sx={sectionTitleSx}
            >
              Zoom Control
            </Typography>
            <Box sx={{ px: 0.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    minWidth: 28,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <ZoomInIcon
                    sx={{
                      color: theme.palette.text.primary,
                      fontSize: 18,
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1, pl: 0.75, pr: 1.5 }}>
                  <Slider
                    value={options.zoomSensitivity}
                    onChange={(_, value) =>
                      onZoomSensitivityChange(value as number)
                    }
                    min={0.1}
                    max={2.0}
                    step={0.1}
                    marks={[
                      { value: 0.1, label: "Fine" },
                      { value: 1.0, label: "Normal" },
                      { value: 2.0, label: "Fast" },
                    ]}
                    sx={{
                      "& .MuiSlider-markLabel": {
                        color: theme.palette.text.primary,
                        fontSize: "0.68rem",
                      },
                      "& .MuiSlider-valueLabel": {
                        backgroundColor:
                          theme.palette.mode === "light"
                            ? "rgba(0, 0, 0, 0.8)"
                            : "rgba(255, 255, 255, 0.8)",
                        color: theme.palette.mode === "light" ? "#fff" : "#000",
                      },
                      "& .MuiSlider-mark": {
                        width: 4,
                        height: 4,
                      },
                      '& .MuiSlider-markLabel[data-index="0"]': {
                        transform: "translateX(22%)",
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>

            <Divider sx={dividerSx} />
          </CardContent>
        </Card>
      </Collapse>
    </Box>
  );
};

export default MapControls;
